import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { questionBankTestService } from '../services/question-bank-test.service';
import { QuestionBankTest } from '../types/question-bank.types';

/**
 * Hook for fetching and managing question bank tests
 */
export function useQuestionBankTests() {
  const [tests, setTests] = useState<QuestionBankTest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all tests for the current user
   */
  const fetchTests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const testsData = await questionBankTestService.getUserTests();
      setTests(testsData);
    } catch (error) {
      console.error('Error fetching tests:', error);
      setError('Failed to load tests');
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch a single test by ID
   */
  const fetchTestById = useCallback(async (testId: string): Promise<QuestionBankTest | null> => {
    // Use a static variable to prevent duplicate calls
    if ((fetchTestById as any).pendingRequests?.[testId]) {
      return (fetchTestById as any).pendingRequests[testId];
    }

    // Create and store the promise
    const promise = (async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await questionBankTestService.getTestById(testId);
        return result;
      } catch (error) {
        console.error('Error fetching test:', error);
        setError('Failed to load test');
        toast.error('Failed to load test');
        return null;
      } finally {
        setLoading(false);
        // Remove the promise reference when finished
        if ((fetchTestById as any).pendingRequests) {
          delete (fetchTestById as any).pendingRequests[testId];
        }
      }
    })();

    // Initialize the pending requests object if it doesn't exist
    if (!(fetchTestById as any).pendingRequests) {
      (fetchTestById as any).pendingRequests = {};
    }

    // Store the promise
    (fetchTestById as any).pendingRequests[testId] = promise;

    return promise;
  }, []);

  /**
   * Generate a new test
   */
  const generateTest = useCallback(async (
    categoryId: string,
    questionCount: number,
    name: string
  ): Promise<QuestionBankTest | null> => {
    try {
      setLoading(true);
      setError(null);

      return await questionBankTestService.generateTest(categoryId, questionCount, name);
    } catch (error) {
      console.error('Error generating test:', error);
      setError('Failed to generate test');
      toast.error('Failed to generate test');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save an answer for a test
   */
  const saveAnswer = useCallback(async (
    testId: string,
    questionId: string,
    selectedOption: string
  ): Promise<any | null> => {
    try {
      return await questionBankTestService.saveAnswer(testId, questionId, selectedOption);
    } catch (error) {
      console.error('Error saving answer:', error);
      toast.error('Failed to save answer');
      return null;
    }
  }, []);

  /**
   * Complete a test
   */
  const completeTest = useCallback(async (testId: string): Promise<QuestionBankTest | null> => {
    try {
      setLoading(true);

      return await questionBankTestService.completeTest(testId);
    } catch (error) {
      console.error('Error completing test:', error);
      toast.error('Failed to complete test');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Restart a test
   */
  const restartTest = useCallback(async (testId: string): Promise<QuestionBankTest | null> => {
    try {
      setLoading(true);

      return await questionBankTestService.restartTest(testId);
    } catch (error) {
      console.error('Error restarting test:', error);
      toast.error('Failed to restart test');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    tests,
    loading,
    error,
    fetchTests,
    fetchTestById,
    generateTest,
    saveAnswer,
    completeTest,
    restartTest,
  };
}
